/**
 * AIShield Milestone 3 Stage B: Aggregation & Metric Utilities
 * Deterministic aggregation anchored strictly to SYNTHETIC_REFERENCE_TIME.
 */

import type { RequestRecord, MetricSummary, ChartBucket, TimeRangeKey } from "../types/telemetry.ts";
import { SYNTHETIC_REFERENCE_TIMESTAMP } from "../data/mockRequests.ts";
import { formatBucketLabelIST } from "./dateTime.ts";

export const INSPECTION_FAILURE_STATUSES = new Set([
  "TIMEOUT",
  "CONNECTION_FAILURE",
  "PARSING_FAILURE",
  "UNEXPECTED_STATUS",
  "MISSING_METADATA",
  "INVALID_METADATA",
  "CONFLICTING_METADATA",
]);

/**
 * Filters requests within the specified time range relative to the fixed reference timestamp.
 */
export function filterByTimeRange(
  requests: RequestRecord[],
  timeRange: TimeRangeKey,
  referenceTimeMs: number = SYNTHETIC_REFERENCE_TIMESTAMP
): RequestRecord[] {
  if (timeRange === "all") {
    return [...requests];
  }

  const durationMinutesMap: Record<"15m" | "60m" | "24h", number> = {
    "15m": 15,
    "60m": 60,
    "24h": 24 * 60,
  };

  const durationMs = durationMinutesMap[timeRange] * 60 * 1000;
  const cutoffMs = referenceTimeMs - durationMs;

  return requests.filter((req) => {
    const reqTime = new Date(req.timestamp).getTime();
    return reqTime >= cutoffMs && reqTime <= referenceTimeMs;
  });
}

export const AI_FAILURE_STATUSES = new Set([
  "TIMEOUT",
  "OVERLOADED",
  "NOT_READY",
  "AUTH_FAILURE",
  "CONNECTION_FAILURE",
  "NETWORK_FAILURE",
  "MALFORMED_RESPONSE",
  "UNEXPECTED_STATUS",
]);

/**
 * Computes deterministic overview metrics from the filtered request slice.
 */
export function computeMetrics(records: RequestRecord[]): MetricSummary {
  const total = records.length;

  let allow = 0;
  let flag = 0;
  let block = 0;
  let notEvaluated = 0;

  let totalTransit = 0;
  let totalUpstream = 0;
  let upstreamCount = 0;

  let timeouts = 0;
  let failed = 0;

  // Stage 4A metrics
  let aiEvaluated = 0;
  let aiNotEvaluated = 0;
  let aiSkipped = 0;
  let aiUnreliable = 0;
  let aiFailure = 0;
  let aiOther = 0;
  let totalAiLatency = 0;
  let aiLatencyCount = 0;

  let crsAttr = 0;
  let aiAttr = 0;
  let bothAttr = 0;
  let unknownAttr = 0;

  let shadowMode = 0;
  let hybridMode = 0;
  let otherMode = 0;

  for (const r of records) {
    // 1. Mutually Exclusive Policy Decision Stacking
    if (r.policy_decision === "ALLOW") {
      allow++;
    } else if (r.policy_decision === "FLAG") {
      flag++;
    } else if (r.policy_decision === "BLOCK") {
      block++;
    } else if (r.policy_decision === null) {
      notEvaluated++;
    }

    // 2. Latency Accumulation
    totalTransit += r.latency_ms;
    if (r.upstream_latency_ms !== null) {
      totalUpstream += r.upstream_latency_ms;
      upstreamCount++;
    }

    // 3. Inspection Failures (7 statuses)
    if (r.inspection_status === "TIMEOUT") {
      timeouts++;
    } else if (INSPECTION_FAILURE_STATUSES.has(r.inspection_status)) {
      failed++;
    }

    // 4. AI Status Partitioning
    const status = r.ai_status;
    if (status === "OK") {
      aiEvaluated++;
      if (r.ai_latency_ms !== null && r.ai_latency_ms !== undefined) {
        totalAiLatency += r.ai_latency_ms;
        aiLatencyCount++;
      }
    } else if (status === null || status === undefined) {
      aiNotEvaluated++;
    } else if (typeof status === "string" && status.startsWith("SKIPPED")) {
      aiSkipped++;
    } else if (status === "UNRELIABLE_REDACTION") {
      aiUnreliable++;
    } else if (typeof status === "string" && AI_FAILURE_STATUSES.has(status)) {
      aiFailure++;
    } else {
      aiOther++;
    }

    // 5. Decision Attribution Partitioning
    const src = r.decision_source;
    if (src === "CRS") {
      crsAttr++;
    } else if (src === "AI") {
      aiAttr++;
    } else if (src === "BOTH") {
      bothAttr++;
    } else {
      unknownAttr++;
    }

    // 6. Recorded Mode Partitioning
    const mode = r.ai_policy_mode;
    if (mode === "SHADOW") {
      shadowMode++;
    } else if (mode === "HYBRID") {
      hybridMode++;
    } else {
      otherMode++;
    }
  }

  // Block rate includes fail-closed policy decisions; null when 0 requests
  const blockRate = total > 0 ? Number(((block / total) * 100).toFixed(1)) : null;

  const avgTransitMs = total > 0 ? Number((totalTransit / total).toFixed(1)) : null;
  const avgUpstreamHeaderMs =
    upstreamCount > 0 ? Number((totalUpstream / upstreamCount).toFixed(1)) : null;

  const aiCoveragePercent =
    total > 0 ? Number(((aiEvaluated / total) * 100).toFixed(1)) : null;
  const avgAiRoundtripMs =
    aiLatencyCount > 0 ? Number((totalAiLatency / aiLatencyCount).toFixed(1)) : null;

  return {
    totalRequests: total,
    blockRate,
    allowCount: allow,
    flagCount: flag,
    blockCount: block,
    notEvaluatedCount: notEvaluated,
    avgTransitMs,
    avgUpstreamHeaderMs,
    timeoutsCount: timeouts,
    failedCount: failed,
    aiEvaluatedCount: aiEvaluated,
    aiNotEvaluatedCount: aiNotEvaluated,
    aiSkippedCount: aiSkipped,
    aiUnreliableRedactionCount: aiUnreliable,
    aiFailureCount: aiFailure,
    aiOtherStatusCount: aiOther,
    aiCoveragePercent,
    avgAiRoundtripMs,
    decisionAttribution: {
      crs: crsAttr,
      ai: aiAttr,
      both: bothAttr,
      unknown: unknownAttr,
    },
    recordedModes: {
      shadow: shadowMode,
      hybrid: hybridMode,
      other: otherMode,
    },
  };
}

/**
 * Returns up to 5 most recent security events or technical gateway failures from the provided slice,
 * ordered stably by timestamp DESC, id DESC.
 */
export function computeRecentSecurityEvents(records: RequestRecord[]): RequestRecord[] {
  const securityEvents = records.filter(
    (r) =>
      r.policy_decision === "FLAG" ||
      r.policy_decision === "BLOCK" ||
      r.gateway_outcome === "GATEWAY_ERROR" ||
      r.gateway_outcome === "REJECTED" ||
      r.response_status === 502
  );
  securityEvents.sort((a, b) => {
    const tA = new Date(a.timestamp).getTime();
    const tB = new Date(b.timestamp).getTime();
    if (tB !== tA) return tB - tA;
    return b.id.localeCompare(a.id);
  });
  return securityEvents.slice(0, 5);
}

/**
 * Generates chart buckets whose span is derived from the actual dataset.
 *
 * For "all" time range: span is derived from the earliest and latest record timestamps,
 * guaranteeing every record appears in exactly one bucket.
 * For bounded ranges (15m, 60m, 24h): span is fixed, anchored to SYNTHETIC_REFERENCE_TIMESTAMP.
 *
 * Invariant: sum of bucket totals === records.length (all records distributed exactly once).
 */
export function generateChartBuckets(
  records: RequestRecord[],
  timeRange: TimeRangeKey,
  referenceTimeMs: number = SYNTHETIC_REFERENCE_TIMESTAMP
): ChartBucket[] {
  if (records.length === 0) {
    // Return minimal empty buckets for chart rendering
    const bucketCount = timeRange === "all" ? 7 : 12;
    const fallbackDurationMs =
      timeRange === "15m"
        ? 15 * 60 * 1000
        : timeRange === "60m"
        ? 60 * 60 * 1000
        : timeRange === "24h"
        ? 24 * 60 * 60 * 1000
        : 7 * 24 * 60 * 60 * 1000;

    const bucketDurationMs = fallbackDurationMs / bucketCount;
    const startWindowMs = referenceTimeMs - fallbackDurationMs;

    return Array.from({ length: bucketCount }, (_, i) => {
      const bucketStart = startWindowMs + i * bucketDurationMs;
      const label = formatBucketLabelIST(bucketStart, timeRange);
      return {
        bucketKey: `bucket-${i}`,
        label,
        startTime: bucketStart,
        endTime: bucketStart + bucketDurationMs,
        allow: 0,
        flag: 0,
        block: 0,
        notEvaluated: 0,
        total: 0,
      };
    });
  }

  let bucketCount: number;
  let startWindowMs: number;
  let endWindowMs: number;

  if (timeRange === "all") {
    // Derive exact span from dataset so every record appears exactly once
    let minTs = Infinity;
    let maxTs = -Infinity;
    for (const r of records) {
      const t = new Date(r.timestamp).getTime();
      if (t < minTs) minTs = t;
      if (t > maxTs) maxTs = t;
    }
    bucketCount = 7;

    if (minTs === maxTs) {
      // All records share same timestamp — place them all in one bucket
      const bucketDurationMs = 60 * 1000; // 1 minute per bucket
      startWindowMs = minTs - 3 * bucketDurationMs;
      endWindowMs = maxTs + 4 * bucketDurationMs;
    } else {
      // Expand range by a small fraction on each side to avoid boundary exclusion
      const rawSpan = maxTs - minTs;
      startWindowMs = minTs - rawSpan * 0.01;
      endWindowMs = maxTs + rawSpan * 0.01;
    }
  } else {
    bucketCount = 12;
    const durationMinutesMap: Record<"15m" | "60m" | "24h", number> = {
      "15m": 15,
      "60m": 60,
      "24h": 24 * 60,
    };
    const rangeDurationMs = durationMinutesMap[timeRange] * 60 * 1000;
    startWindowMs = referenceTimeMs - rangeDurationMs;
    endWindowMs = referenceTimeMs;
  }

  const bucketDurationMs = (endWindowMs - startWindowMs) / bucketCount;

  const buckets: ChartBucket[] = [];

  for (let i = 0; i < bucketCount; i++) {
    const bucketStart = startWindowMs + i * bucketDurationMs;
    const bucketEnd = bucketStart + bucketDurationMs;

    // Format human label in IST
    const label = formatBucketLabelIST(bucketStart, timeRange);

    buckets.push({
      bucketKey: `bucket-${i}`,
      label,
      startTime: bucketStart,
      endTime: bucketEnd,
      allow: 0,
      flag: 0,
      block: 0,
      notEvaluated: 0,
      total: 0,
    });
  }

  // Distribute records into buckets — every record must land in exactly one bucket.
  // For "all" range, the span fully covers all records by construction.
  // Last bucket absorbs its upper boundary inclusively.
  for (const r of records) {
    const t = new Date(r.timestamp).getTime();
    let placed = false;
    for (let i = 0; i < buckets.length; i++) {
      const b = buckets[i];
      const isMatch =
        i === buckets.length - 1
          ? t >= b.startTime && t <= b.endTime
          : t >= b.startTime && t < b.endTime;

      if (isMatch) {
        b.total++;
        if (r.policy_decision === "ALLOW") {
          b.allow++;
        } else if (r.policy_decision === "FLAG") {
          b.flag++;
        } else if (r.policy_decision === "BLOCK") {
          b.block++;
        } else {
          b.notEvaluated++;
        }
        placed = true;
        break;
      }
    }
    // Safety: if a record somehow falls outside all bucket boundaries (edge case),
    // clamp it into the nearest bucket — preserves invariant that sum === records.length
    if (!placed) {
      const clampIdx = t < buckets[0].startTime ? 0 : buckets.length - 1;
      const b = buckets[clampIdx];
      b.total++;
      if (r.policy_decision === "ALLOW") {
        b.allow++;
      } else if (r.policy_decision === "FLAG") {
        b.flag++;
      } else if (r.policy_decision === "BLOCK") {
        b.block++;
      } else {
        b.notEvaluated++;
      }
    }
  }

  return buckets;
}
